import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        <p className="text-gray-500 mb-8">Last updated: February 2026</p>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              By accessing or using Red Panda Socials ("Platform"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our services. We reserve the right to modify 
              these terms at any time, and your continued use of the Platform constitutes acceptance of any changes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Eligibility</h2>
            <p className="text-gray-600 leading-relaxed">
              You must be at least 18 years old and capable of forming a binding contract to use our Platform. 
              By using Red Panda Socials, you represent and warrant that you meet these eligibility requirements. 
              Users under 18 may only use the Platform under the supervision of a parent or legal guardian.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Account Registration</h2>
            <p className="text-gray-600 leading-relaxed">
              To access certain features, you must create an account using Google Sign-In. You are responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
              <li>Providing accurate and complete information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Guest Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              As a Guest booking accommodations through our Platform, you agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li>Use properties only for lawful purposes and in accordance with house rules</li>
              <li>Treat properties with respect and report any damages immediately</li>
              <li>Not exceed the maximum occupancy specified in the listing</li>
              <li>Comply with check-in and check-out times</li>
              <li>Not engage in any illegal activities on the premises</li>
              <li>Be responsible for the conduct of all guests in your party</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Host Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              As a Host listing properties on our Platform, you agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li>Provide accurate and truthful information about your property</li>
              <li>Maintain your property in a safe, clean, and habitable condition</li>
              <li>Honor all confirmed bookings unless cancellation is unavoidable</li>
              <li>Comply with all applicable laws, regulations, and tax obligations</li>
              <li>Have appropriate insurance coverage for your property</li>
              <li>Respond to guest inquiries in a timely manner</li>
              <li>Not discriminate against guests based on race, religion, gender, or nationality</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Booking and Payments</h2>
            <p className="text-gray-600 leading-relaxed">
              All bookings are subject to availability and Host confirmation. Payment terms include:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li>Full payment is required at the time of booking</li>
              <li>Payments are processed securely through Razorpay</li>
              <li>A 15% service fee is charged on each booking</li>
              <li>Hosts receive 85% of the booking amount after guest check-in</li>
              <li>All prices are listed in Indian Rupees (INR)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Cancellation Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              Cancellation policies vary by listing. Generally:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li>Full refund if cancelled before 30 days before check-in</li>
              <li>75% refund if cancelled before 15 days before check-in</li>
              <li>50% refund if cancelled 7 days before check-in</li>
              <li>No refund if cancelled before 6 days before check-in</li>
              <li>Service fees are non-refundable</li>
              <li>No-shows forfeit the entire booking amount</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Prohibited Activities</h2>
            <p className="text-gray-600 leading-relaxed">
              Users are prohibited from:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li>Creating fake accounts or providing false information</li>
              <li>Engaging in fraudulent transactions</li>
              <li>Harassing, threatening, or abusing other users</li>
              <li>Posting misleading or deceptive content</li>
              <li>Circumventing the Platform to avoid fees</li>
              <li>Using the Platform for any illegal purpose</li>
              <li>Scraping or collecting user data without consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              Red Panda Socials acts as an intermediary platform connecting Guests and Hosts. We are not responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li>The condition, legality, or safety of listed properties</li>
              <li>The accuracy of listing information provided by Hosts</li>
              <li>Any injuries, damages, or losses incurred during stays</li>
              <li>Disputes between Guests and Hosts</li>
              <li>Acts of God, natural disasters, or unforeseen circumstances</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              Our total liability shall not exceed the amount paid for the specific booking in question.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed">
              All content on the Platform, including logos, designs, text, and graphics, is the property of 
              Red Panda Socials or its licensors. Users may not copy, reproduce, or distribute any content 
              without prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Dispute Resolution</h2>
            <p className="text-gray-600 leading-relaxed">
              Any disputes arising from these terms shall be resolved through:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li>Good faith negotiation between parties</li>
              <li>Mediation if negotiation fails</li>
              <li>Arbitration under the Arbitration and Conciliation Act, 1996</li>
              <li>Courts of Surat, Gujarat shall have exclusive jurisdiction</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms of Service shall be governed by and construed in accordance with the laws of India. 
              Any legal action shall be brought exclusively in the courts located in Surat, Gujarat.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Contact Information</h2>
            <p className="text-gray-600 leading-relaxed">
              For any questions regarding these Terms of Service, please contact us at:
            </p>
            <div className="mt-4 bg-gray-100 p-4 rounded-lg">
              <p className="text-gray-700"><strong>Red Panda Socials</strong></p>
              <p className="text-gray-600">Email: admin@redpandasocials.com</p>
              <p className="text-gray-600">Phone: +91 8799095561</p>
              <p className="text-gray-600">Address: Surat, Gujarat, India</p>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Terms;
